import type { ExcalidrawElementSkeleton } from "@excalidraw/element";

/**
 * Mermaid renders labels as HTML by default, so definitions commonly contain
 * markup — most notably `<br/>` for line breaks, but also inline formatting
 * such as `<b>`/`<i>`/`<span>`.
 *
 * `mermaid-to-excalidraw` returns the raw label text, which would end up in the
 * canvas verbatim (e.g. `Line 1<br/>Line 2`). We therefore convert the
 * supported subset of HTML into plain text before creating the elements.
 */

// tags translated into a line break (only the closing tag of a block-level
// element produces the break, so `<p>a</p><p>b</p>` yields `a\nb`)
const LINE_BREAK_TAGS = ["p", "div", "li", "tr", "h1", "h2", "h3", "h4", "h5", "h6"]; // prettier-ignore

// tags removed without leaving a trace
const STRIPPED_TAGS = [
  "a",
  "abbr",
  "b",
  "big",
  "blockquote",
  "center",
  "code",
  "del",
  "em",
  "font",
  "i",
  "ins",
  "kbd",
  "mark",
  "ol",
  "pre",
  "q",
  "s",
  "samp",
  "small",
  "span",
  "strike",
  "strong",
  "sub",
  "sup",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tt",
  "u",
  "ul",
  "var",
];

const tagsRegex = (tagNames: string[], opts?: { closingOnly?: boolean }) =>
  new RegExp(
    `<\\s*${opts?.closingOnly ? "" : "\\/?"}\\s*(?:${tagNames.join(
      "|",
    )})(?:\\s[^<>]*)?\\s*\\/?\\s*>`,
    "gi",
  );

const BR_REGEX = /<\s*br\s*\/?\s*>/gi;
const LINE_BREAK_TAGS_REGEX = tagsRegex(LINE_BREAK_TAGS, { closingOnly: true });
const REMOVED_TAGS_REGEX = tagsRegex([...LINE_BREAK_TAGS, ...STRIPPED_TAGS]);
const HR_REGEX = /<\s*hr\s*\/?\s*>/gi;

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: " ",
  quot: '"',
};

const decodeHtmlEntities = (text: string) =>
  text.replace(
    /&(#x[0-9a-f]+|#\d+|[a-z]+);/gi,
    (entity, code: string): string => {
      if (code[0] === "#") {
        const codePoint =
          code[1] === "x" || code[1] === "X"
            ? parseInt(code.slice(2), 16)
            : parseInt(code.slice(1), 10);

        if (!Number.isFinite(codePoint) || codePoint <= 0) {
          return entity;
        }

        try {
          return String.fromCodePoint(codePoint);
        } catch {
          return entity;
        }
      }

      return NAMED_ENTITIES[code.toLowerCase()] ?? entity;
    },
  );

/**
 * Converts the HTML mermaid supports inside labels into plain text
 * (`<br/>` → newline, formatting tags stripped, entities decoded).
 */
export const mermaidHtmlToText = (text: string): string => {
  if (!text || !/[<&]/.test(text)) {
    return text;
  }

  const converted = decodeHtmlEntities(
    text
      .replace(BR_REGEX, "\n")
      .replace(HR_REGEX, "\n")
      .replace(LINE_BREAK_TAGS_REGEX, "\n")
      .replace(REMOVED_TAGS_REGEX, ""),
  );

  return (
    converted
      .replace(/\r\n?/g, "\n")
      // whitespace around a line break is an artifact of the markup
      .replace(/[ \t]*\n[ \t]*/g, "\n")
      .replace(/^\n+|\n+$/g, "")
  );
};

/**
 * Converts the HTML in labels of the elements returned by
 * `mermaid-to-excalidraw` into plain text.
 */
export const convertMermaidElementsHtmlToText = (
  elements: readonly ExcalidrawElementSkeleton[],
): ExcalidrawElementSkeleton[] =>
  elements.map((element) => {
    let nextElement = element;

    if (
      "text" in nextElement &&
      typeof nextElement.text === "string" &&
      nextElement.text
    ) {
      const text = mermaidHtmlToText(nextElement.text);
      if (text !== nextElement.text) {
        nextElement = { ...nextElement, text };
      }
    }

    if (
      "label" in nextElement &&
      nextElement.label &&
      typeof nextElement.label.text === "string" &&
      nextElement.label.text
    ) {
      const text = mermaidHtmlToText(nextElement.label.text);
      if (text !== nextElement.label.text) {
        nextElement = {
          ...nextElement,
          label: { ...nextElement.label, text },
        };
      }
    }

    return nextElement;
  });
